import React from "react";

const NAV_ITEMS = [
  { key: "about", label: "About" },
  { key: "facilities", label: "Facilities" },
  { key: "grounds", label: "Grounds" },
  { key: "gallery", label: "Gallery" },
  { key: "location", label: "Location" },
];

const ClassicTheme = ({
  venue,
  grounds = [],
  website,
  backgroundStyle = "clean",
  primaryColor = "#33A444",
  accentColor = "#111827",
  heroImage = "",
  headline,
  subheadline,
  ctaLabel = "Book Now",
  openBooking,
  sectionRenderers = {},
  themeLayout = "classic",
  selectedThemeLayout = "classic",
}) => {
  const visibleNavigation = NAV_ITEMS.filter(
    (item) => website?.sections?.[item.key] !== false
  );

  const orderedSections = Array.isArray(website?.sectionOrder)
    ? website.sectionOrder
        .filter((key) => key !== "hero")
        .filter((key) => website?.sections?.[key] !== false)
    : [];

  const bookingEnabled = website?.booking?.enabled !== false;

  const city =
    venue?.location?.city ||
    venue?.city ||
    "Your city";

  const openingTime = venue?.policies?.openTime || "06:00";
  const closingTime = venue?.policies?.closeTime || "22:00";

  return (
    <main
      id="top"
      className={`pv-page pv-theme-${themeLayout} pv-bg-${backgroundStyle}`}
      data-theme={themeLayout}
      data-selected-theme={selectedThemeLayout}
      style={{
        "--pv-primary": primaryColor,
        "--pv-accent": accentColor,
      }}
    >
      <header className="pvc-nav-shell">
        <nav className="pvc-nav" aria-label="Venue website navigation">
          <a className="pvc-brand" href="#top" aria-label={venue?.name || "Venue"}>
            <span className="pvc-brand-mark" aria-hidden="true">
              <span />
            </span>

            <span className="pvc-brand-copy">
              <strong>{venue?.name || "Venue"}</strong>
              <small>{city}</small>
            </span>
          </a>

          <div className="pvc-nav-links">
            <a href="#top">Home</a>

            {visibleNavigation.map((item) => (
              <a href={`#${item.key}`} key={item.key}>
                {item.label}
              </a>
            ))}
          </div>

          {bookingEnabled && (
            <button
              type="button"
              className="pvc-nav-cta"
              onClick={() => openBooking?.()}
            >
              {ctaLabel}
            </button>
          )}
        </nav>
      </header>

      {website?.sections?.hero !== false && (
        <section className="pvc-hero">
          <div className="pvc-hero-copy">
            <span className="pvc-eyebrow">Premium sports experience</span>

            <h1>{headline}</h1>

            <p>{subheadline}</p>

            <div className="pvc-hero-actions">
              {bookingEnabled && (
                <button
                  type="button"
                  className="pvc-primary-action"
                  onClick={() => openBooking?.()}
                >
                  {ctaLabel}
                </button>
              )}

              {venue?.googleMapsUrl && (
                <a
                  className="pvc-secondary-action"
                  href={venue.googleMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View Location
                </a>
              )}
            </div>

            <div className="pvc-trust-row">
              <span>
                <strong>{grounds.length}</strong>
                <small>
                  Ground{grounds.length === 1 ? "" : "s"}
                </small>
              </span>

              <span>
                <strong>{openingTime}</strong>
                <small>Opens daily</small>
              </span>

              <span>
                <strong>{closingTime}</strong>
                <small>Closing time</small>
              </span>
            </div>
          </div>

          <div className="pvc-hero-visual">
            <div className="pvc-hero-frame">
              {heroImage ? (
                <img src={heroImage} alt={venue?.name || "Sports venue"} />
              ) : (
                <div className="pvc-hero-placeholder">
                  <span>Welcome to</span>
                  <strong>{venue?.name || "Your venue"}</strong>
                </div>
              )}

              <div className="pvc-image-overlay" />

              <div className="pvc-availability-card">
                <span className="pvc-availability-dot" />

                <div>
                  <strong>Bookings available</strong>
                  <small>
                    {openingTime} – {closingTime}
                  </small>
                </div>
              </div>
            </div>

            <div className="pvc-hero-decoration pvc-hero-decoration--one" />
            <div className="pvc-hero-decoration pvc-hero-decoration--two" />
          </div>
        </section>
      )}

      <section className="pvc-feature-strip" aria-label="Venue highlights">
        <div>
          <span>01</span>
          <div>
            <strong>Easy online booking</strong>
            <small>Select one or multiple available slots.</small>
          </div>
        </div>

        <div>
          <span>02</span>
          <div>
            <strong>Quality facilities</strong>
            <small>Everything required for a great game.</small>
          </div>
        </div>

        <div>
          <span>03</span>
          <div>
            <strong>Convenient location</strong>
            <small>{city}</small>
          </div>
        </div>
      </section>

      <div className="pv-content pvc-content">
        {orderedSections.map((key) => {
          const renderSection = sectionRenderers[key];

          return renderSection ? (
            <React.Fragment key={key}>
              {renderSection()}
            </React.Fragment>
          ) : null;
        })}
      </div>

      <footer className="pvc-footer">
        <div className="pvc-footer-brand">
          <span className="pvc-brand-mark" aria-hidden="true">
            <span />
          </span>

          <div>
            <strong>{venue?.name || "Venue"}</strong>
            <small>{city}</small>
          </div>
        </div>

        <p>
          View facilities, check availability and reserve your preferred
          sports slot online.
        </p>

        <div className="pvc-footer-bottom">
          <span>© {new Date().getFullYear()} {venue?.name || "Venue"}</span>
          <span>Powered by OnGround</span>
        </div>
      </footer>
    </main>
  );
};

export default ClassicTheme;